import ContentLayout from '@cloudscape-design/components/content-layout';
import {
  Alert,
  Box,
  Button,
  Container,
  Flashbar,
  Header,
  Modal,
  SpaceBetween,
  StatusIndicator,
} from '@cloudscape-design/components';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ManageAssigneesModal from 'src/components/audit/ManageAssigneesModal';
import AssigneeAvatars from 'src/components/audit/AssigneeAvatars';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import routes from 'src/contstant/routePaths';
import useUserProfile from 'src/hooks/useUserProfile';
import { getRumInstance } from 'src/rum';
import { useAudits } from 'src/store/audits/useAudits';
import { useQuestionResponses } from 'src/store/questionResponses/useQuestionResponses';
import Divider from 'src/components/divider/Divider';
import { Audit } from 'src/types/helios/Audit';
import PerformAuditQuestionsList from 'src/components/audit/perform-audit/PerformAuditQuestionsList';
import PerformAuditQuestionView, {
  PerformAuditQuestionViewHandle,
} from 'src/components/audit/perform-audit/PerformAuditQuestionView';
import ApiStatusFlashBar from 'src/components/ApiStatusFlashBar';
import { useSplitPanel } from 'src/context/SplitPanelContext';
import {
  AUDIT_TYPE_LABELS,
  AuditStatus,
  AuditTypeOptions,
  DEFAULT_LANGUAGE,
  POLL_INTERVAL_MS,
  TaskType,
} from 'src/contstant/commonLabels';
import { formatDateTime } from 'src/utils/generalUtils';
import CreateTaskForm from 'src/components/task/CreateTaskForm';
import { useBaseHook } from 'src/hooks/useBaseHook';
import permissionAccessConfig from 'src/contstant/permissionAccessConfig';
import { useUsers } from 'src/store/users/useUsers';
import { useTasks } from 'src/store/tasks/useTasks';
import {
  getIsPerformAudit,
  getAuditDisableReason,
  getAuditAssignees,
} from 'src/helper/performAuditHelpers';
import { isEqual, toLower } from 'lodash';
import { LanguageDropdown } from 'src/components/LanguageDropdown';
import { getAudiSummaryUrl } from '../audits/auditsConfig';
import {
  hasAuditMandatoryFailViolations,
  hasAuditMissingNaReasonViolations,
} from 'src/utils/auditClosureValidationUtils';
import { useListReasonsQuery } from 'src/data/api/helios-service-ts/reason/queries/useListReasonsQuery';
import { ReasonCategory } from '@amzn/gbsa-helios-typescript-client';
import { useFeatureFlags } from 'src/context/FeatureFlagsContext';
import { useQuestionResponsePoller } from 'src/hooks/useQuestionResponsePoller';

/**
 * PerformAuditPage - Interface for conducting audits by answering questions
 * Allows users to navigate through audit questions, assign audits, and complete them
 */
const PerformAuditPage = () => {
  // Navigation and routing
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const auditId = searchParams.get('auditId');
  const businessUnit = searchParams.get('businessUnit');
  if (!auditId || !businessUnit) {
    navigate(routes.AUDITS.path, { replace: true });
    return <></>;
  }
  const basehook = useBaseHook();

  // Custom hooks for data management
  const {
    updateAudit,
    updateAuditApiStatus,
    resetApiStatus,
    getAuditById,
    currentAudit,
  } = useAudits();
  const {
    getUserId,
    getPersona,
    getUserProgramId,
    getUserBusinessUnitId,
    getDivisions,
    getUserSelectedLanguage,
    getPreferLanguageList,
    selectUserLanguage,
  } = useUserProfile();
  const { setContent, setTitle, closeView, content, setPanelSize } = useSplitPanel();

  const {
    auditQuestionsData,
    getAllApiStatus,
    updateQuestionResponseApiStatus,
    currentQuestionIndex,
    setCurrentQuestion,
    getAllQuestionResponses,
    auditQuestionResponses,
    resetStatus,
    filteredQuestionIds,
  } = useQuestionResponses();
  const { checkUserPermissions, userPermission, currentUser } = useUsers();
  const { createTaskApiStatus, createTaskFromQuestionApiStatus, clearAPIStatusMessage } =
    useTasks();

  // Get the displayed questions - respects pie chart filter
  const auditQuestions = useMemo(() => {
    if (filteredQuestionIds === null) {
      return auditQuestionsData.questions;
    }
    return auditQuestionsData.questions.filter((q) =>
      filteredQuestionIds.includes(q.questionId),
    );
  }, [auditQuestionsData.questions, filteredQuestionIds]);

  // Component state
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false); // Task creation modal visibility
  const [isConfirmTaskModalOpen, setIsConfirmTaskModalOpen] = useState(false);
  const [isAssigneeModalVisible, setAssigneeModalVisible] = useState(false); // Assignee modal visibility
  const hasInitializedAssignee = useRef(false);
  const questionViewRef = useRef<PerformAuditQuestionViewHandle>(null);

  // Store the full unfiltered questions list for violation checking
  // auditQuestionsData.questions gets replaced when any filter is applied (pie chart, keyword, etc.)
  // We preserve the largest set seen, since filtered results are always a subset
  const allQuestionsRef = useRef(auditQuestionsData.questions);
  useEffect(() => {
    if (
      auditQuestionsData.questions.length > 0 &&
      auditQuestionsData.questions.length >= allQuestionsRef.current.length
    ) {
      allQuestionsRef.current = auditQuestionsData.questions;
    }
  }, [auditQuestionsData.questions]);

  const isPerformAudit = useCallback(() => {
    return getIsPerformAudit(getPersona(), userPermission, currentAudit);
  }, [getPersona, userPermission, currentAudit]);

  const getDisableReason = useCallback((): string => {
    return getAuditDisableReason(getUserId(), isPerformAudit(), currentAudit);
  }, [currentAudit, getUserId, isPerformAudit]);

  const isReadOnlyMode = useCallback((): boolean => {
    const assignees = getAuditAssignees(currentAudit);
    const userIsAssignee = assignees.some((a) =>
      isEqual(toLower(a), toLower(getUserId())),
    );
    return (
      currentAudit?.status === AuditStatus.COMPLETED ||
      assignees.length === 0 ||
      !isPerformAudit() ||
      !userIsAssignee
    );
  }, [getUserId, currentAudit, isPerformAudit]);

  /**
   * Gets the business unit ID for the current audit
   * @returns {string} Business unit ID from URL parameters
   */
  const getAuditBusinessUnitId = () => {
    return businessUnit;
  };

  /**
   * Checks if audit can be closed
   * Enabled when not in read-only mode and all questions are answered
   */
  const isEnableCloseAudit = useCallback(() => {
    return (
      !isReadOnlyMode() &&
      auditQuestionResponses.filter((q) => q.responseResult).length ===
        auditQuestionResponses.length
    );
  }, [isReadOnlyMode(), auditQuestionResponses]);

  // Feature flags
  const { enableEnhancedResponseTypes } = useFeatureFlags();

  /**
   * Checks if any failed questions have missing mandatory comment/evidence.
   * Used to show a warning and block audit closure from the UI side.
   */
  const mandatoryFailViolationsExist = useMemo(() => {
    return hasAuditMandatoryFailViolations(
      allQuestionsRef.current,
      auditQuestionResponses,
      enableEnhancedResponseTypes,
    );
  }, [allQuestionsRef.current, auditQuestionResponses, enableEnhancedResponseTypes]);

  // Poll question responses for multi-user collaboration awareness
  useQuestionResponsePoller({
    auditId,
    businessUnitId: businessUnit,
    enabled: true,
  });

  // Poll audit metadata (assignees, status) for multi-user collaboration
  useEffect(() => {
    const interval = setInterval(() => {
      if (!document.hidden) {
        getAuditById({
          businessUnitId: businessUnit,
          auditId,
        });
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [auditId, businessUnit, getAuditById]);

  // Fetch NA reasons for the audit's division to validate closure
  const { data: reasonsData } = useListReasonsQuery(
    {
      businessUnitId: businessUnit,
      category: ReasonCategory.NA_REASON,
      divisionName: currentAudit?.divisionName ?? '',
      activeOnly: true,
    },
    { enabled: !!currentAudit?.divisionName },
  );

  /**
   * Checks if any NA-answered questions are missing a required NA reason.
   * Only applies when NA reasons are configured for the division.
   */
  const missingNaReasonViolationsExist = useMemo(() => {
    const naReasonsExist = (reasonsData?.reasons ?? []).length > 0;
    return hasAuditMissingNaReasonViolations(auditQuestionResponses, naReasonsExist);
  }, [reasonsData, auditQuestionResponses]);

  /**
   * Effect: Navigate to summary page when audit is successfully completed
   * Monitors audit update status and redirects to summary on completion
   */
  useEffect(() => {
    if (
      updateAuditApiStatus.isSuccess &&
      updateAuditApiStatus.updatedAudit?.status === AuditStatus.COMPLETED
    ) {
      openAuditSummary(currentAudit!);
    }
    return () => {
      resetApiStatus();
    };
  }, [updateAuditApiStatus.isSuccess]);

  /**
   * Navigates to the audit summary page with audit details
   * @param {Audit} item - The audit object containing ID and business unit
   */
  const openAuditSummary = (item: Audit) => {
    const params = new URLSearchParams({
      auditId: item.auditId,
      businessUnit: item.businessUnitId!,
    });
    navigate(`${routes.AUDIT_SUMMARY.path}?${params}`);
  };

  /**
   * Effect: Load audit questions and audit details on page initialization
   * Fetches question responses and audit data based on URL parameters
   */
  useEffect(() => {
    getAllQuestionResponses({
      businessUnitId: getAuditBusinessUnitId(),
      auditId: auditId,
    });
    getAuditById({
      businessUnitId: getAuditBusinessUnitId(),
      auditId: auditId,
    });
    return () => {
      resetStatus();
    };
  }, [getAuditBusinessUnitId(), auditId]);

  /**
   * Effect: Check user permissions for different audit types
   * Validates user access to calibration, progress, and situation audits
   */
  useEffect(() => {
    checkUserPermissions({
      userId: getUserId(),
      programId: getUserProgramId(),
      businessUnit: getUserBusinessUnitId(),
      permissions: [
        permissionAccessConfig.CALIBRATION_AUDIT.PERFORM_CALIBRATION_AUDIT,
        permissionAccessConfig.PROGRESS_AUDIT.PERFORM_PROGRESS_AUDIT,
        permissionAccessConfig.SITUATION_AUDIT.PERFORM_SITUATION_AUDIT,
        permissionAccessConfig.MPA_AUDIT.PERFORM_MPA_AUDIT,
        permissionAccessConfig.TEST_AUDIT.PERFORM_TEST_AUDIT,
      ],
      divisions: getDivisions(),
    });
  }, [currentUser]);

  /**
   * Effect: Initialize page state and UI components
   * Opens filter view and sets up cleanup for question navigation
   */
  useEffect(() => {
    openFilterView();
    return () => {
      setCurrentQuestion(0);
      closeView();
    };
  }, []);

  /**
   * Effect: Track audit start for adoption and time-spent metrics via RUM
   */
  useEffect(() => {
    if (currentAudit?.auditId && currentAudit?.location) {
      getRumInstance()
        .then((rum) =>
          rum?.recordEvent('audit_start', {
            auditId: currentAudit.auditId,
            businessUnit: currentAudit.businessUnit,
            location: currentAudit.location,
            userId: getUserId(),
            startedAt: new Date().toISOString(),
          }),
        )
        .catch(() => {}); // Non-blocking: RUM failure must not affect audit flow
    }
  }, [currentAudit?.auditId]);

  /**
   * Effect: Show the assignee management modal on initial load when the audit
   * has no assignees. Only fires once per mount — subsequent audit updates
   * (e.g. from polling) do not reopen the modal.
   */
  useEffect(() => {
    if (currentAudit && !hasInitializedAssignee.current) {
      hasInitializedAssignee.current = true;
      setAssigneeModalVisible(getAuditAssignees(currentAudit).length === 0);
    }
  }, [currentAudit]);

  /**
   * Opens the split panel with questions list for filtering/navigation
   */
  const openFilterView = () => {
    setPanelSize(window.innerWidth * 0.2);
    setTitle('Audit Question Response');
    setContent(
      <PerformAuditQuestionsList auditId={auditId} businessUnitId={businessUnit} />,
    );
  };
  /**
   * Renders the page header with audit information and action buttons
   * Includes audit type, expiry date, assignee management, and audit controls
   */
  const renderPageHeader = (
    <div className="perform-audit-header-content" data-testid="perform-audit-header">
      <div className="perform-audit-header-left">
        <h3 style={{ margin: 0 }}>{currentAudit?.name}</h3>
        <span
          data-testid="audit-description"
          className="perform-audit-header-description"
        >
          Audit Type:{' '}
          {AUDIT_TYPE_LABELS[currentAudit?.auditType as keyof typeof AUDIT_TYPE_LABELS]}
          {'   '}
          <strong>
            <span>• </span>
          </strong>
          {'   '}
          Audit Expiry:{' '}
          {formatDateTime(auditQuestionsData.auditMetadata.expirationDate, 'DD MMM YYYY')}
        </span>
      </div>
      <div className="perform-audit-header-right" data-testid="header-actions">
        <LanguageDropdown
          data-testid="language-dropdown"
          selectedLanguage={getUserSelectedLanguage()}
          defaultLanguage={DEFAULT_LANGUAGE}
          filterLanguges={getPreferLanguageList()}
          onLanguageChange={(value) => {
            selectUserLanguage(value);
          }}
        />
        <AssigneeAvatars
          assignees={getAuditAssignees(currentAudit)}
          activeUser={getUserId()}
          onManageClick={() => setAssigneeModalVisible(true)}
          disabled={
            currentAudit?.status === AuditStatus.COMPLETED ||
            currentAudit?.status === AuditStatus.EXPIRED
          }
        />
        <Button
          data-testid="generate-task-button"
          disabled={
            currentAudit?.status === AuditStatus.COMPLETED ||
            currentAudit?.status === AuditStatus.EXPIRED
          }
          variant="normal"
          onClick={() => setIsConfirmTaskModalOpen(true)}
        >
          Create Audit Observation
        </Button>
        {currentAudit?.status !== AuditStatus.COMPLETED && (
          <Button
            data-testid="close-audit-button"
            variant="primary"
            disabled={
              !isEnableCloseAudit() ||
              mandatoryFailViolationsExist ||
              missingNaReasonViolationsExist
            }
            loading={updateAuditApiStatus.isLoading && isEnableCloseAudit()}
            onClick={() => {
              // Flush any unsaved comments before closing the audit
              questionViewRef.current?.flushPendingComments();

              getRumInstance().then((rum) =>
                rum?.recordEvent('audit_end', {
                  auditId: auditId,
                  businessUnit: currentAudit?.businessUnit,
                  location: currentAudit?.location,
                  userId: getUserId(),
                  completedAt: new Date().toISOString(),
                }),
              );
              updateAudit({
                auditId: auditId,
                status: AuditStatus.COMPLETED,
                businessUnitId: getAuditBusinessUnitId(),
                lastUpdatedBy: getUserId(),
                completionDate: new Date().toISOString(),
              });
            }}
          >
            Close Audit
          </Button>
        )}

        {currentAudit?.status === AuditStatus.COMPLETED && (
          <Button
            data-testid="view-summary-button"
            variant="primary"
            href={getAudiSummaryUrl(currentAudit)}
            onClick={(event) => {
              event.preventDefault();
              openAuditSummary(currentAudit);
            }}
          >
            View Summary
          </Button>
        )}
        {!content && (
          <Button
            data-testid="filter-button"
            iconName="filter"
            onClick={() => {
              openFilterView();
            }}
          >
            Filter
          </Button>
        )}
      </div>
    </div>
  );

  /**
   * Renders navigation buttons for moving between questions
   */
  const renderFooter = () => {
    const noQuestions = auditQuestions.length === 0;
    return (
      <SpaceBetween
        data-testid="footer-navigation"
        direction="horizontal"
        size="m"
        alignItems="center"
      >
        <Button
          data-testid="previous-button"
          disabled={noQuestions || currentQuestionIndex === 0}
          onClick={() => setCurrentQuestion(Math.max(0, currentQuestionIndex - 1))}
        >
          Previous
        </Button>
        <Button
          data-testid="next-button"
          disabled={noQuestions || currentQuestionIndex === auditQuestions.length - 1}
          onClick={() =>
            setCurrentQuestion(
              Math.min(auditQuestions.length - 1, currentQuestionIndex + 1),
            )
          }
        >
          Next
        </Button>
      </SpaceBetween>
    );
  };
  /**
   * Handles multi-assignee update from ManageAssigneesModal (called on modal close)
   */
  const handleMultiAssigneeSubmit = () => {
    setAssigneeModalVisible(false);
  };

  /**
   * Renders loading state while fetching audit data
   * Shows loading flashbar with progress indicator
   */
  if (getAllApiStatus.isLoading) {
    return (
      <Flashbar
        data-testid="loading-flashbar"
        items={[
          {
            type: 'info',
            loading: true,
            header: 'Loading in Progress',
            content: 'Loading question details. This may take a few moments.',
            id: 'loading',
          },
        ]}
      />
    );
  }

  return (
    <div className="perform-audit-page-layout">
      {/* Warning alert for missing mandatory comment/evidence on pass/fail questions */}
      {mandatoryFailViolationsExist &&
        isEnableCloseAudit() &&
        currentAudit?.status !== AuditStatus.COMPLETED && (
          <Alert
            data-testid="mandatory-fail-warning"
            type="warning"
            statusIconAriaLabel="Warning"
          >
            Audit cannot be closed. Mandatory comments or evidence are missing for one or
            more questions. Please complete before closing the audit.
          </Alert>
        )}
      {/* Warning alert for missing NA reasons on NA-answered questions */}
      {missingNaReasonViolationsExist &&
        isEnableCloseAudit() &&
        currentAudit?.status !== AuditStatus.COMPLETED && (
          <Alert
            data-testid="missing-na-reason-warning"
            type="warning"
            statusIconAriaLabel="Warning"
          >
            Audit cannot be closed. One or more questions marked as Not Applicable are
            missing a required Not Applicable reason. Please select a reason for each Not
            Applicable question before closing the audit.
          </Alert>
        )}
      <div className="perform-audit-header">{renderPageHeader}</div>
      <Container
        data-testid="perform-audit-container"
        className="perform-audit-question-view-main-container"
        fitHeight
      >
        <ManageAssigneesModal
          data-testid="manage-assignees-modal"
          auditId={auditId}
          currentAssignees={getAuditAssignees(currentAudit)}
          businessUnitId={getAuditBusinessUnitId()}
          visible={isAssigneeModalVisible}
          onDismiss={() => setAssigneeModalVisible(false)}
          onSubmit={handleMultiAssigneeSubmit}
        />
        <ApiStatusFlashBar
          data-testid="api-status-flashbar"
          apiStatus={[
            getAllApiStatus,
            updateQuestionResponseApiStatus,
            updateAuditApiStatus,
            createTaskApiStatus,
            createTaskFromQuestionApiStatus,
          ]}
          clearMessages={() => {
            resetStatus();
            resetApiStatus();
            clearAPIStatusMessage();
          }}
        />

        <Modal
          data-testid="generate-task-confirmation-modal"
          visible={isConfirmTaskModalOpen}
          onDismiss={() => setIsConfirmTaskModalOpen(false)}
          header="Generate Observational Task for this Audit"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={() => setIsConfirmTaskModalOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setIsConfirmTaskModalOpen(false);
                    setIsTaskModalOpen(true);
                  }}
                >
                  Continue
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <Alert type="warning" statusIconAriaLabel="Warning">
            <b>Heads up!</b> You're about to create a task for the entire audit. If you'd
            like to create a task for a <b>failed audit question</b>, finish the audit and
            navigate to the <b>Summary page</b> to do so.
          </Alert>
        </Modal>

        {currentAudit && (
          <CreateTaskForm
            data-testid="create-task-form"
            onDismiss={() => setIsTaskModalOpen(false)}
            taskType={TaskType.OBSERVATION}
            audit={currentAudit}
            onSave={(data) => {
              setIsTaskModalOpen(false);
            }}
            visible={isTaskModalOpen}
          />
        )}

        {currentAudit && (
          <PerformAuditQuestionView
            ref={questionViewRef}
            data-testid="perform-audit-question-view"
            isReadOnlyMode={isReadOnlyMode()}
            audit={currentAudit}
            disableReason={getDisableReason()}
          />
        )}
      </Container>
      <div data-testid="perform-audit-footer" className="perform-audit-footer">
        {renderFooter()}
      </div>
    </div>
  );
};

export default PerformAuditPage;
